import { createContext, useContext } from "react";
import type { ReactNode, RefObject } from "react";

import { modules } from "byzantine";

import { useIsCollapsedSearchBarVisible, useSearchBarListeners } from "./hooks";

const { useGlobalSearch } = modules.search;

type SearchBarContextValue = ReturnType<typeof useGlobalSearch> & {
  isSearchBarCollapsedVisible: boolean;
  isOpen: boolean;
  toggleOpen: () => void;
  setClosed: () => void;
  paneRef: RefObject<HTMLDivElement | null>;
};

const SearchBarContext = createContext<SearchBarContextValue | null>(null);

export const SearchBarProvider = ({ children }: { children: ReactNode }) => {
  const { isOpen, toggleOpen, setClosed, paneRef } = useSearchBarListeners();
  const isSearchBarCollapsedVisible = useIsCollapsedSearchBarVisible();
  const globalSearch = useGlobalSearch();

  return (
    <SearchBarContext.Provider
      value={{
        isOpen,
        toggleOpen,
        setClosed,
        paneRef,
        isSearchBarCollapsedVisible,
        ...globalSearch,
      }}
    >
      {children}
    </SearchBarContext.Provider>
  );
};

export const useSearchBar = (): SearchBarContextValue => {
  const ctx = useContext(SearchBarContext);
  if (!ctx) {
    throw new Error("useSearchBar must be used within a SearchBarProvider");
  }
  return ctx;
};
